class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    // UNIFYTMP: i4us/r4us auto shipping-protection flow. getSelectedVariant() is
    // defined by the store-specific cart-shipping-protection.js asset (deployed only
    // on the i4us/r4us branches and loaded by sections/main-cart-footer.liquid behind
    // the settings.unifytmp_enable_cart_fake_checkout_button gate), so this
    // typeof guard is a no-op on the other stores. Proper fix: replace the
    // decoy-checkout-button/JS shipping-protection flow with an app block or Shopify
    // Function, then delete this guard, the asset and the metafield.
    if (typeof getSelectedVariant === "function") getSelectedVariant();
    // UNIFYTMP: end
    // F5/F6 cart-side delivery: migrated to assets/delivery-estimate.js (cart path) on modern,
    // which reads #cart-variant-data + subscribes to cartUpdate. legacy-us/t4l still define the
    // global estDeliveryDate(), so the typeof guard keeps them working unchanged.
    if (typeof estDeliveryDate === "function") estDeliveryDate();
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
        if (typeof estDeliveryDate === "function") estDeliveryDate(); // modern: delivery-estimate.js cart path handles via cartUpdate
        // UNIFYTMP: recompute the i4us/r4us shipping-protection variant after a line
        // change; getSelectedVariant() only exists where the store-specific
        // cart-shipping-protection.js asset is deployed (see the constructor note).
        if (typeof getSelectedVariant === "function") getSelectedVariant();
        // UNIFYTMP: end
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// accept alternative + offload confimration

document.addEventListener("DOMContentLoaded", function () {
  const cartForm = document.querySelector('form[action="/cart"]');
  if (!cartForm) return;

  function setupRadioValidation(groupName, wrapperClass) {
    const wrapper = document.querySelector(`.${wrapperClass}`);
    if (!wrapper) return null;

    const radioButtons = wrapper.querySelectorAll(`input[name="${groupName}"]`);
    if (!radioButtons.length) return null;

    let errorSpan = wrapper.querySelector(".error-message");
    if (!errorSpan) {
      errorSpan = document.createElement("span");
      errorSpan.classList.add("error-message");
      errorSpan.style.color = "red";
      errorSpan.style.marginLeft = "10px";
      errorSpan.style.fontSize = "0.9em";
      errorSpan.textContent = "Please select an option";
      errorSpan.style.display = "none";

      const spans = wrapper.querySelectorAll("span");
      if (spans.length > 0) {
        spans[spans.length - 1].insertAdjacentElement("afterend", errorSpan);
      } else {
        wrapper.appendChild(errorSpan);
      }
    }

    return {
      validate: function () {
        const isSelected = Array.from(radioButtons).some((rb) => rb.checked);
        if (!isSelected) {
          errorSpan.style.display = "inline";
          wrapper.style.border = "2px solid red";
          wrapper.style.borderRadius = "5px";
          radioButtons[0].focus();
          return false;
        } else {
          errorSpan.style.display = "none";
          wrapper.style.border = "none";
          return true;
        }
      },
      getSelectedValue: function () {
        const selected = wrapper.querySelector(
          `input[name="${groupName}"]:checked`
        );
        return selected ? selected.value : null;
      },
      groupName: groupName,
    };
  }

  // Setup validation and attribute collection for each group
  const altValidation = setupRadioValidation(
    "attributes[accept_alt]",
    "accept_alt"
  );
  const offloadValidation = setupRadioValidation(
    "attributes[offload_confirmation]",
    "offload_confirmation"
  );

  cartForm.addEventListener("submit", function (e) {
    let isFormValid = true;

    if (altValidation && !altValidation.validate()) isFormValid = false;
    if (offloadValidation && !offloadValidation.validate()) isFormValid = false;

    if (!isFormValid) {
      e.preventDefault();
      return;
    }

    // Remove existing attribute inputs to avoid duplicates
    const existingInputs = cartForm.querySelectorAll(
      'input[name^="attributes["]'
    );
    existingInputs.forEach((input) => input.remove());

    // Add selected values as hidden inputs
    [altValidation, offloadValidation].forEach((validationObj) => {
      if (validationObj) {
        const val = validationObj.getSelectedValue();
        if (val) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = validationObj.groupName; // already has attributes[...] format
          input.value = val;
          cartForm.appendChild(input);
        }
      }
    });
  });
  // Fast Checkout compatiblity (no validation)
  function getSelectedValue(groupName) {
    const input = document.querySelector(`input[name="${groupName}"]:checked`);
    return input ? input.value : null;
  }

  function updateCartAttributes(attributes) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ attributes }),
    });
  }

  function handleFastCheckoutClick(event) {
    const acceptAlt = getSelectedValue("attributes[accept_alt]");
    const offloadConf = getSelectedValue("attributes[offload_confirmation]");

    const attrs = {};
    if (acceptAlt) attrs["accept_alt"] = acceptAlt;
    if (offloadConf) attrs["offload_confirmation"] = offloadConf;

    if (Object.keys(attrs).length === 0) return; // nothing to save

    event.preventDefault(); // try to delay the fast checkout

    updateCartAttributes(attrs).finally(() => {
      event.target.click(); // re-trigger the original button after update
    });
  }

  function observeFastCheckoutButtons() {
    const alreadyHandled = new WeakSet();

    const tryAttachHandlers = () => {
      const buttons = document.querySelectorAll(
        '[data-shopify-button="shop-pay-button"], .shopify-payment-button__button, #dynamic-checkout-cart button'
      );

      buttons.forEach((button) => {
        if (!alreadyHandled.has(button)) {
          alreadyHandled.add(button);
          button.addEventListener("click", handleFastCheckoutClick, {
            once: true,
          });
        }
      });
    };

    tryAttachHandlers();

    // Watch for dynamic changes (e.g. fast checkout button appears later)
    const observer = new MutationObserver(() => {
      tryAttachHandlers();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  observeFastCheckoutButtons();
});

// disable scroll wheel from altering numerical input values
document.addEventListener('wheel', function(e) {
  if (document.activeElement.type === 'number') {
    document.activeElement.blur();
  }
}, { passive: false });
